import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Plus, Minus, AlertTriangle, Loader2, Ban } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
interface ModifyItem {
  id: number | string;
  item_name_snapshot: string;
  quantity: number;
  unit_price_snapshot: number;
  line_total: number;
  status: string;
  notes: string | null;
}
interface CabanaModifyOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  /** Called after any successful modification or cancellation */
  onChanged: () => void;
  /** Called when the user cancels the ENTIRE order */
  onCancelOrder: (orderId: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
}
/**
 * Cabaña Parrillera — exclusive cashier tool to modify or cancel an
 * order that is already in progress (incl. while it is in the kitchen).
 *
 * Scope:
 *  - Change item quantities (incl. removing items entirely).
 *  - Cancel the full order with one click.
 *
 * Safety:
 *  - All edits run as targeted supabase updates and never touch
 *    unrelated columns. The orders.subtotal and orders.total are
 *    recomputed from the surviving (non-cancelled) items + the
 *    existing delivery_fee so payment screens stay accurate.
 */
export function CabanaModifyOrderModal({
  isOpen,
  onClose,
  orderId,
  onChanged,
  onCancelOrder
}: CabanaModifyOrderModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<ModifyItem[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderStatus, setOrderStatus] = useState<string>('');
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [cancellingAll, setCancellingAll] = useState(false);
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  // Load order + items whenever the modal opens
  useEffect(() => {
    if (!isOpen || !orderId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const {
          data: order,
          error: ordErr
        } = await supabase.from('orders').select('id, order_number, status, delivery_fee').eq('id', orderId).maybeSingle();
        if (ordErr) throw ordErr;
        if (cancelled) return;
        if (!order) {
          setError('No se encontró la orden');
          setItems([]);
          return;
        }
        setOrderNumber(order.order_number || '');
        setOrderStatus(order.status || '');
        setDeliveryFee(Number(order.delivery_fee) || 0);
        const {
          data: rows,
          error: itemsErr
        } = await supabase.from('order_items').select('id, item_name_snapshot, quantity, unit_price_snapshot, line_total, status, notes').eq('order_id', orderId).order('id', {
          ascending: true
        });
        if (itemsErr) throw itemsErr;
        if (cancelled) return;
        setItems((rows || []).map(r => ({
          id: r.id,
          item_name_snapshot: r.item_name_snapshot,
          quantity: Number(r.quantity) || 0,
          unit_price_snapshot: Number(r.unit_price_snapshot) || 0,
          line_total: Number(r.line_total) || 0,
          status: r.status || 'new',
          notes: r.notes
        })));
      } catch (e: any) {
        if (!cancelled) {
          console.error('[CabanaModifyOrderModal] load error:', e);
          setError(e?.message || 'Error al cargar la orden');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    // Reset confirm-all gate each time we open
    setConfirmCancelAll(false);
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId]);
  const activeItems = useMemo(() => items.filter(it => it.status !== 'cancelled'), [items]);
  const cancelledItems = useMemo(() => items.filter(it => it.status === 'cancelled'), [items]);
  const subtotal = useMemo(() => activeItems.reduce((sum, it) => sum + (Number(it.line_total) || 0), 0), [activeItems]);
  const total = subtotal + deliveryFee;
  // Recompute the order's subtotal/total from the surviving items
  const recomputeOrderTotals = async (orderId: string, nextItems: ModifyItem[]) => {
    const newSubtotal = nextItems.filter(it => it.status !== 'cancelled').reduce((sum, it) => sum + (Number(it.line_total) || 0), 0);
    const newTotal = newSubtotal + deliveryFee;
    const {
      error: updErr
    } = await supabase.from('orders').update({
      subtotal: newSubtotal,
      total: newTotal
    }).eq('id', orderId);
    if (updErr) throw updErr;
  };
  const handleQtyChange = async (itemId: number | string, delta: number) => {
    if (!orderId || saving) return;
    const target = items.find(it => it.id === itemId);
    if (!target) return;
    const newQty = target.quantity + delta;
    if (newQty < 0) return;
    setSaving(true);
    setError(null);
    try {
      if (newQty === 0) {
        // Treat as removal
        const {
          error: itErr
        } = await supabase.from('order_items').update({
          status: 'cancelled'
        }).eq('id', itemId);
        if (itErr) throw itErr;
        const next = items.map(it => it.id === itemId ? {
          ...it,
          status: 'cancelled'
        } : it);
        setItems(next);
        await recomputeOrderTotals(orderId, next);
      } else {
        const newLineTotal = newQty * target.unit_price_snapshot;
        const {
          error: itErr
        } = await supabase.from('order_items').update({
          quantity: newQty,
          line_total: newLineTotal
        }).eq('id', itemId);
        if (itErr) throw itErr;
        const next = items.map(it => it.id === itemId ? {
          ...it,
          quantity: newQty,
          line_total: newLineTotal
        } : it);
        setItems(next);
        await recomputeOrderTotals(orderId, next);
      }
      onChanged();
    } catch (e: any) {
      console.error('[CabanaModifyOrderModal] qty change error:', e);
      setError(e?.message || 'No se pudo actualizar la cantidad');
    } finally {
      setSaving(false);
    }
  };
  const handleRemoveItem = async (itemId: number | string) => {
    if (!orderId || saving) return;
    if (!window.confirm('¿Quitar este artículo de la orden? Esta acción no se puede deshacer.')) return;
    setSaving(true);
    setError(null);
    try {
      const {
        error: itErr
      } = await supabase.from('order_items').update({
        status: 'cancelled'
      }).eq('id', itemId);
      if (itErr) throw itErr;
      const next = items.map(it => it.id === itemId ? {
        ...it,
        status: 'cancelled'
      } : it);
      setItems(next);
      await recomputeOrderTotals(orderId, next);
      onChanged();
    } catch (e: any) {
      console.error('[CabanaModifyOrderModal] remove error:', e);
      setError(e?.message || 'No se pudo quitar el artículo');
    } finally {
      setSaving(false);
    }
  };
  const handleCancelEntireOrder = async () => {
    if (!orderId) return;
    setCancellingAll(true);
    setError(null);
    try {
      const res = await onCancelOrder(orderId);
      if (!res.success) {
        setError(res.error || 'No se pudo cancelar la orden');
        return;
      }
      onChanged();
      onClose();
    } catch (e: any) {
      console.error('[CabanaModifyOrderModal] cancel-all error:', e);
      setError(e?.message || 'No se pudo cancelar la orden');
    } finally {
      setCancellingAll(false);
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-390">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-391">
        <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 40,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 28
      }} className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-392">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-393">
            <div data-id="element-394">
              <h2 className="text-lg font-black text-gray-900 leading-tight" data-id="element-395">
                Modificar / Cancelar
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-396">
                Orden #{orderNumber || orderId} · {orderStatus}
              </p>
            </div>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-397">
              <X className="w-5 h-5" data-id="element-398" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-399">
            {loading ? <div className="flex items-center justify-center py-16 text-gray-400" data-id="element-400">
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-401" />
              </div> : error ? <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2" data-id="element-402">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-403" />
                <span data-id="element-404">{error}</span>
              </div> : null}

            {!loading && activeItems.length === 0 && !error && <div className="py-10 text-center text-gray-500 text-sm" data-id="element-405">
                Esta orden ya no tiene artículos activos.
              </div>}

            {/* Active items */}
            <div className="space-y-3" data-id="element-406">
              {activeItems.map(item => <div key={String(item.id)} className="border border-gray-200 rounded-xl p-3 flex gap-3" data-id="element-407">
                  <div className="flex-1 min-w-0" data-id="element-408">
                    <p className="font-bold text-gray-900 truncate" data-id="element-409">
                      {item.item_name_snapshot}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-410">
                      {formatCurrency(item.unit_price_snapshot)} c/u
                    </p>
                    {item.notes && <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2" data-id="element-411">
                        Nota: {item.notes}
                      </p>}
                  </div>

                  <div className="flex flex-col items-end justify-between shrink-0" data-id="element-412">
                    <span className="font-bold text-gray-900" data-id="element-413">
                      {formatCurrency(item.line_total)}
                    </span>
                    <div className="flex items-center gap-1 mt-2" data-id="element-414">
                      <button onClick={() => handleQtyChange(item.id, -1)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 active:scale-95 transition-all" aria-label="Disminuir" data-id="element-415">
                        <Minus className="w-4 h-4" data-id="element-416" />
                      </button>
                      <span className="w-8 text-center font-bold text-gray-900 tabular-nums" data-id="element-417">
                        {item.quantity}
                      </span>
                      <button onClick={() => handleQtyChange(item.id, +1)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 active:scale-95 transition-all" aria-label="Aumentar" data-id="element-418">
                        <Plus className="w-4 h-4" data-id="element-419" />
                      </button>
                      <button onClick={() => handleRemoveItem(item.id)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40 active:scale-95 transition-all ml-1" aria-label="Quitar artículo" data-id="element-420">
                        <Trash2 className="w-4 h-4" data-id="element-421" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Cancelled items (informational) */}
            {cancelledItems.length > 0 && <div className="mt-5" data-id="element-422">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2" data-id="element-423">
                  Quitados de la orden
                </p>
                <div className="space-y-1.5" data-id="element-424">
                  {cancelledItems.map(item => <div key={String(item.id)} className="flex items-center justify-between text-sm text-gray-400 line-through px-3 py-1.5 bg-gray-50 rounded-lg" data-id="element-425">
                      <span className="truncate pr-2" data-id="element-426">
                        {item.quantity}x {item.item_name_snapshot}
                      </span>
                      <span className="shrink-0" data-id="element-427">
                        {formatCurrency(item.line_total)}
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>

          {/* Totals + actions */}
          <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 shrink-0" data-id="element-428">
            <div className="space-y-1 mb-4" data-id="element-429">
              <div className="flex justify-between text-sm text-gray-600" data-id="element-430">
                <span data-id="element-431">Subtotal</span>
                <span className="font-medium tabular-nums" data-id="element-432">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              {deliveryFee > 0 && <div className="flex justify-between text-sm text-gray-600" data-id="element-433">
                  <span data-id="element-434">Express</span>
                  <span className="font-medium tabular-nums" data-id="element-435">
                    {formatCurrency(deliveryFee)}
                  </span>
                </div>}
              <div className="flex justify-between text-lg font-black text-gray-900 pt-1 border-t border-gray-200 mt-1" data-id="element-436">
                <span data-id="element-437">Total</span>
                <span className="tabular-nums" data-id="element-438">{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Cancel-entire-order — two-step confirmation */}
            {!confirmCancelAll ? <button onClick={() => setConfirmCancelAll(true)} disabled={saving || cancellingAll} className="w-full h-12 rounded-xl font-bold text-sm bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-439">
                <Ban className="w-4 h-4" data-id="element-440" />
                Cancelar pedido completo
              </button> : <div className="space-y-2" data-id="element-441">
                <p className="text-sm text-red-700 text-center font-medium" data-id="element-442">
                  ¿Seguro que quieres cancelar TODO el pedido?
                </p>
                <div className="grid grid-cols-2 gap-2" data-id="element-443">
                  <button onClick={() => setConfirmCancelAll(false)} disabled={cancellingAll} className="h-12 rounded-xl font-bold text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50" data-id="element-444">
                    No, mantener
                  </button>
                  <button onClick={handleCancelEntireOrder} disabled={cancellingAll} className="h-12 rounded-xl font-bold text-sm bg-red-600 hover:bg-red-700 text-white active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-445">
                    {cancellingAll ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-446" /> : <Ban className="w-4 h-4" data-id="element-447" />}
                    Sí, cancelar todo
                  </button>
                </div>
              </div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}